import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogIn, LogOut, Camera, RefreshCw, ArrowLeft, CheckCircle2, CameraOff, Loader2, Coffee, Play } from "lucide-react";
import { toast } from "sonner";
import { getKioskOperators, kioskClock, fmtDuration } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/landing/ThemeToggle";
import { LogoMark, LogoWordmark } from "@/components/landing/Logo";

const timeOf = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function Kiosk() {
  const { t } = useT();
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("grid"); // grid | camera | preview | success
  const [selected, setSelected] = useState(null); // { operator_id, name, action }
  const [captured, setCaptured] = useState(null); // { blob, url }
  const [cameraError, setCameraError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const loadOperators = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getKioskOperators();
      setOperators(data.operators || []);
    } catch (e) {
      console.error("kiosk: load operators failed", e);
      toast.error(t("clock.actionFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadOperators(); }, [loadOperators]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      console.error("kiosk: camera error", e);
      setCameraError(true);
    }
  }, []);

  // Manage camera lifecycle based on stage
  useEffect(() => {
    if (stage === "camera") {
      startCamera();
    } else {
      stopCamera();
    }
    return () => { if (stage === "camera") stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Cleanup on unmount + revoke object URLs
  useEffect(() => () => stopCamera(), [stopCamera]);
  useEffect(() => () => { if (captured?.url) URL.revokeObjectURL(captured.url); }, [captured]);

  const beginAction = (op, action) => {
    setSelected({ operator_id: op.operator_id, name: op.name, action });
    setCaptured(null);
    setStage("camera");
  };

  // Labels per action, reused across camera header, confirm button and success.
  const actionLabel = (action) => ({
    in: t("clock.clockIn"), out: t("clock.clockOut"),
    break_start: t("clock.startBreak"), break_end: t("clock.endBreak"),
  }[action] || "");

  const cancelFlow = () => {
    if (captured?.url) URL.revokeObjectURL(captured.url);
    setCaptured(null);
    setSelected(null);
    setStage("grid");
  };

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      if (captured?.url) URL.revokeObjectURL(captured.url);
      setCaptured({ blob, url: URL.createObjectURL(blob) });
      setStage("preview");
    }, "image/jpeg", 0.85);
  };

  const retake = () => {
    if (captured?.url) URL.revokeObjectURL(captured.url);
    setCaptured(null);
    setStage("camera");
  };

  const confirm = async () => {
    if (!selected || !captured) return;
    setBusy(true);
    try {
      const r = await kioskClock(selected.operator_id, selected.action, captured.blob);
      const msg = {
        in: t("clock.kioskSuccessIn", { name: selected.name }),
        out: t("clock.kioskSuccessOut", { name: selected.name, dur: fmtDuration(r.duration_seconds) }),
        break_start: t("clock.kioskSuccessBreakStart", { name: selected.name }),
        break_end: t("clock.kioskSuccessBreakEnd", { name: selected.name }),
      }[selected.action] || "";
      setSuccessMsg(msg);
      setStage("success");
      if (captured?.url) URL.revokeObjectURL(captured.url);
      setCaptured(null);
      setTimeout(() => {
        setSelected(null);
        setSuccessMsg("");
        setStage("grid");
        loadOperators();
      }, 2600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("clock.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="kiosk-page">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <LogoMark className="h-8 w-8" />
            <LogoWordmark />
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost" size="sm" onClick={loadOperators}
              className="text-muted-foreground hover:text-foreground"
              data-testid="kiosk-refresh-button"
            >
              <RefreshCw className="mr-1.5 h-4 w-4" /> {t("clock.kioskRefresh")}
            </Button>
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {/* Title */}
        <div className="mb-8 flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="kiosk-title">
            {t("clock.kioskTitle")}
          </h1>
          <p className="text-sm text-muted-foreground sm:text-base">
            {stage === "grid" ? t("clock.kioskSubtitle") : t("clock.kioskPickPrompt")}
          </p>
        </div>

        {/* STAGE: GRID */}
        {stage === "grid" && (
          <>
            {loading ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-32 animate-pulse rounded-[var(--radius)] border border-border bg-secondary/50" />
                ))}
              </div>
            ) : operators.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center text-sm text-muted-foreground" data-testid="kiosk-empty">
                  {t("clock.kioskNoOps")}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {operators.map((op) => (
                  <div
                    key={op.operator_id}
                    data-testid={`kiosk-op-${op.operator_id}`}
                    className="flex flex-col rounded-[var(--radius)] border border-border bg-card p-5 text-left shadow-[0_10px_30px_hsl(var(--foreground)/0.06)]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-lg font-semibold">{op.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{op.title || "\u00A0"}</div>
                      </div>
                      {op.on_break ? (
                        <Badge className="badge-warn shrink-0">{t("clock.onBreak")}</Badge>
                      ) : op.clocked_in ? (
                        <Badge className="badge-ok shrink-0">{t("clock.clockedIn")}</Badge>
                      ) : (
                        <Badge className="shrink-0 border border-border bg-secondary text-muted-foreground">{t("clock.notClockedIn")}</Badge>
                      )}
                    </div>
                    {op.on_break && op.break_since ? (
                      <div className="mt-1 text-xs text-muted-foreground">{t("clock.onBreakSince", { time: timeOf(op.break_since) })}</div>
                    ) : op.clocked_in && op.since ? (
                      <div className="mt-1 text-xs text-muted-foreground">{t("clock.since", { time: timeOf(op.since) })}</div>
                    ) : null}

                    <div className="mt-4 flex flex-wrap gap-2">
                      {!op.clocked_in && (
                        <Button
                          size="sm" onClick={() => beginAction(op, "in")}
                          className="bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-[hsl(var(--brand-hover))]"
                          data-testid={`kiosk-op-in-${op.operator_id}`}
                        >
                          <LogIn className="mr-1.5 h-4 w-4" /> {t("clock.clockIn")}
                        </Button>
                      )}
                      {op.clocked_in && !op.on_break && (
                        <Button
                          variant="outline" size="sm" onClick={() => beginAction(op, "break_start")}
                          data-testid={`kiosk-op-break-start-${op.operator_id}`}
                        >
                          <Coffee className="mr-1.5 h-4 w-4" /> {t("clock.startBreak")}
                        </Button>
                      )}
                      {op.clocked_in && op.on_break && (
                        <Button
                          size="sm" onClick={() => beginAction(op, "break_end")}
                          className="bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-[hsl(var(--brand-hover))]"
                          data-testid={`kiosk-op-break-end-${op.operator_id}`}
                        >
                          <Play className="mr-1.5 h-4 w-4" /> {t("clock.endBreak")}
                        </Button>
                      )}
                      {op.clocked_in && (
                        <Button
                          variant="outline" size="sm" onClick={() => beginAction(op, "out")}
                          data-testid={`kiosk-op-out-${op.operator_id}`}
                        >
                          <LogOut className="mr-1.5 h-4 w-4" /> {t("clock.clockOut")}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* STAGE: CAMERA / PREVIEW */}
        {(stage === "camera" || stage === "preview") && selected && (
          <Card className="overflow-hidden" data-testid="kiosk-camera-card">
            <CardContent className="p-5 sm:p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold">{selected.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {actionLabel(selected.action)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={cancelFlow} data-testid="kiosk-cancel-button">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("clock.kioskBack")}
                </Button>
              </div>

              <div className="relative mx-auto aspect-[4/3] w-full max-w-xl overflow-hidden rounded-[var(--radius)] border border-border bg-black">
                {/* Live video */}
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  className={`h-full w-full object-cover ${stage === "preview" ? "hidden" : ""}`}
                  style={{ transform: "scaleX(-1)" }}
                  data-testid="kiosk-video"
                />
                {/* Captured preview */}
                {stage === "preview" && captured && (
                  <img
                    src={captured.url}
                    alt="capture"
                    className="h-full w-full object-cover"
                    style={{ transform: "scaleX(-1)" }}
                    data-testid="kiosk-preview-image"
                  />
                )}
                {/* Camera error overlay */}
                {stage === "camera" && cameraError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
                    <CameraOff className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground" data-testid="kiosk-camera-error">{t("clock.kioskCameraError")}</p>
                    <Button variant="outline" size="sm" onClick={startCamera} data-testid="kiosk-start-camera">
                      <Camera className="mr-1.5 h-4 w-4" /> {t("clock.kioskStartCamera")}
                    </Button>
                  </div>
                )}
              </div>

              <canvas ref={canvasRef} className="hidden" />

              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                {stage === "camera" ? (
                  <Button
                    size="lg" onClick={takePhoto} disabled={cameraError}
                    className="bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-[hsl(var(--brand-hover))]"
                    data-testid="kiosk-take-photo"
                  >
                    <Camera className="mr-2 h-5 w-5" /> {t("clock.kioskTakePhoto")}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="lg" onClick={retake} disabled={busy} data-testid="kiosk-retake">
                      <RefreshCw className="mr-2 h-4 w-4" /> {t("clock.kioskRetake")}
                    </Button>
                    <Button
                      size="lg" onClick={confirm} disabled={busy}
                      className="bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-[hsl(var(--brand-hover))]"
                      data-testid="kiosk-confirm"
                    >
                      {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : (
                        selected.action === "in" ? <LogIn className="mr-2 h-5 w-5" />
                          : selected.action === "out" ? <LogOut className="mr-2 h-5 w-5" />
                          : selected.action === "break_start" ? <Coffee className="mr-2 h-5 w-5" />
                          : <Play className="mr-2 h-5 w-5" />
                      )}
                      {{
                        in: t("clock.kioskConfirmIn"), out: t("clock.kioskConfirmOut"),
                        break_start: t("clock.kioskConfirmBreakStart"), break_end: t("clock.kioskConfirmBreakEnd"),
                      }[selected.action]}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* STAGE: SUCCESS */}
        {stage === "success" && (
          <Card data-testid="kiosk-success">
            <CardContent className="flex flex-col items-center justify-center gap-4 py-20 text-center">
              <CheckCircle2 className="h-16 w-16 text-[hsl(var(--success))]" />
              <p className="text-xl font-semibold">{successMsg}</p>
              <p className="text-sm text-muted-foreground">{t("clock.kioskDone")}</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
